import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Trophy, MagnifyingGlass, Funnel, Buildings, Users, Medal } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function Leaderboard() {
  const { API } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [collegeLeaders, setCollegeLeaders] = useState([]);
  const [userStanding, setUserStanding] = useState(null);
  const [examFilter, setExamFilter] = useState('ALL');
  const [institutionFilter, setInstitutionFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [institutions, setInstitutions] = useState([]);
  const [activeTab, setActiveTab] = useState('candidates');
  
  const [targetYearFilter, setTargetYearFilter] = useState('ALL');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const { data } = await axios.get(`${API}/dashboard/institutions`, { withCredentials: true });
        setInstitutions(data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchInstitutions();
  }, [API]);

  useEffect(() => {
    const fetchLeaders = async () => {
      try {
        if (activeTab === 'candidates') {
          const { data } = await axios.get(`${API}/dashboard/leaderboard`, { 
            params: { exam: examFilter, search: debouncedSearch, institution: institutionFilter, target_year: targetYearFilter },
            withCredentials: true 
          });
          setLeaders(data.leaderboard);
          setUserStanding(data.user_standing);
        } else {
          const { data } = await axios.get(`${API}/dashboard/college-leaderboard`, { 
            params: { exam: examFilter, target_year: targetYearFilter },
            withCredentials: true 
          });
          setCollegeLeaders(data);
        }
      } catch (err) {
        console.error("Failed to fetch leaderboard", err);
      }
    };
    fetchLeaders();
  }, [API, examFilter, debouncedSearch, institutionFilter, targetYearFilter, activeTab]);

  return (
    <div className="container py-8 max-w-5xl space-y-8" data-testid="leaderboard-page">
      <div className="text-center space-y-4">
        <Trophy size={64} className="mx-auto text-chart-3" weight="duotone" />
        <h1 className="text-5xl font-heading uppercase text-white tracking-widest">ALL INDIA STANDINGS</h1>
        <p className="text-muted-foreground font-bold uppercase tracking-widest text-sm">Real-time Annual Rankings</p>
      </div>

      <Tabs defaultValue="candidates" onValueChange={setActiveTab} className="w-full">
        <div className="flex justify-center mb-8">
          <TabsList className="bg-black/50 border border-white/10 rounded-none h-14 p-1">
            <TabsTrigger value="candidates" className="rounded-none h-full font-heading text-lg tracking-widest uppercase data-[state=active]:bg-primary data-[state=active]:text-white px-8">
              CANDIDATES
            </TabsTrigger>
            <TabsTrigger value="colleges" className="rounded-none h-full font-heading text-lg tracking-widest uppercase data-[state=active]:bg-chart-3 data-[state=active]:text-background px-8">
              COLLEGES
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="candidates" className="space-y-8 mt-0">
          {userStanding ? (
            <Card className="rounded-none border border-primary bg-primary/10 mb-8" data-testid="user-standing-card">
              <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-heading text-white tracking-widest uppercase">YOUR CURRENT STANDING</h2>
                  <p className="text-sm font-bold uppercase tracking-widest text-primary">Based on {examFilter === 'ALL' ? 'All Exams' : examFilter}</p>
                </div>
                <div className="flex items-center gap-6 md:gap-8">
                  <div className="text-center">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">RANK</p>
                    <p className="text-3xl md:text-4xl font-black text-chart-3 font-heading">#{userStanding.rank}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">TIME/Q</p>
                    <p className="text-3xl md:text-4xl font-black text-primary font-heading">{userStanding.avg_time_per_q}s</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">ACCURACY</p>
                    <p className="text-3xl md:text-4xl font-black text-chart-5 font-heading">{userStanding.accuracy}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-none border border-dashed border-border bg-black/20 mb-8" data-testid="no-standing-card">
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground font-bold tracking-widest text-sm uppercase">COMPLETE A {examFilter === 'ALL' ? '' : examFilter} MOCK TEST THIS YEAR TO ESTABLISH YOUR STANDING</p>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-card p-4 border border-border">
            <div className="relative w-full md:w-1/3">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
              <Input 
                placeholder="SEARCH CANDIDATE..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-black/50 border-white/10 rounded-none tracking-wider text-white w-full"
                data-testid="search-leaderboard"
              />
            </div>
            
            <div className="relative w-full md:w-1/3">
              <Buildings className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
              <Input 
                list="institutions-list"
                placeholder="FILTER BY COLLEGE..." 
                value={institutionFilter}
                onChange={(e) => setInstitutionFilter(e.target.value)}
                className="pl-10 h-12 bg-black/50 border-white/10 rounded-none tracking-wider text-white w-full"
                data-testid="college-filter"
              />
              <datalist id="institutions-list">
                {institutions.map((inst, i) => (
                  <option key={i} value={inst} />
                ))}
              </datalist>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto">
              <Funnel className="text-muted-foreground" size={20} />
              <select 
                className="h-12 bg-black/50 border border-white/10 rounded-none tracking-wider text-white px-4 outline-none focus:border-primary w-full md:w-[150px]"
                value={targetYearFilter}
                onChange={(e) => setTargetYearFilter(e.target.value)}
                data-testid="year-filter"
              >
                <option value="ALL">ALL BATCHES</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
                <option value="2028">2028</option>
              </select>
              <select 
                className="h-12 bg-black/50 border border-white/10 rounded-none tracking-wider text-white px-4 outline-none focus:border-primary w-full md:w-[150px]"
                value={examFilter}
                onChange={(e) => setExamFilter(e.target.value)}
                data-testid="exam-filter"
              >
                <option value="ALL">ALL EXAMS</option>
                <option value="JEE">JEE</option>
                <option value="NEET">NEET</option>
                <option value="IAS">IAS</option>
                <option value="CAT">CAT</option>
              </select>
            </div>
          </div>

          <Card className="rounded-none border-border bg-card overflow-x-auto">
            <div className="min-w-[800px]">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-border bg-secondary font-bold text-xs uppercase tracking-widest text-muted-foreground hidden md:grid">
                <div className="col-span-1 text-center">RANK</div>
                <div className="col-span-3">CANDIDATE</div>
                <div className="col-span-3">COLLEGE</div>
                <div className="col-span-2 text-center">EXAM</div>
                <div className="col-span-1 text-center">TIME/Q</div>
                <div className="col-span-2 text-right">ACCURACY</div>
              </div>
              <div className="divide-y divide-border">
                {leaders.map(l => (
                  <div key={l.user_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 p-4 items-center hover:bg-secondary/30 transition-colors">
                    <div className="col-span-1 md:col-span-1 flex items-center gap-2 md:block">
                      <span className="md:hidden text-xs text-muted-foreground font-bold tracking-widest">RANK: </span>
                      <span className="font-heading text-2xl text-primary md:text-center block">#{l.rank}</span>
                    </div>
                    <div className="col-span-1 md:col-span-3 font-bold truncate" title={l.name}>{l.name}</div>
                    <div className="col-span-1 md:col-span-3 text-muted-foreground text-sm truncate" title={l.institution}>
                      <span className="md:hidden font-bold mr-2 uppercase tracking-widest text-xs">COLLEGE:</span>{l.institution}
                    </div>
                    <div className="col-span-1 md:col-span-2 md:text-center text-xs tracking-widest uppercase md:border md:border-border md:px-2 md:py-1 md:mx-auto md:bg-secondary truncate text-muted-foreground md:text-foreground">
                      <span className="md:hidden font-bold mr-2">EXAM:</span>{l.exam}
                    </div>
                    <div className="col-span-1 md:col-span-1 md:text-center text-xs text-muted-foreground font-bold">
                      <span className="md:hidden mr-2 tracking-widest uppercase">TIME/Q:</span>{l.avg_time_per_q}s
                    </div>
                    <div className="col-span-1 md:col-span-2 md:text-right font-bold text-chart-5">
                      <span className="md:hidden text-xs text-muted-foreground mr-2 tracking-widest uppercase">ACCURACY:</span>{l.accuracy}%
                    </div>
                  </div>
                ))}
                {leaders.length === 0 && <div className="p-8 text-center text-muted-foreground tracking-widest uppercase text-sm font-bold">NO STANDINGS AVAILABLE</div>}
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="colleges" className="space-y-8 mt-0">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-end bg-card p-4 border border-border">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <Funnel className="text-muted-foreground" size={20} />
              <select 
                className="h-12 bg-black/50 border border-white/10 rounded-none tracking-wider text-white px-4 outline-none focus:border-primary w-full md:w-[150px]"
                value={targetYearFilter}
                onChange={(e) => setTargetYearFilter(e.target.value)}
              >
                <option value="ALL">ALL BATCHES</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
                <option value="2028">2028</option>
              </select>
              <select 
                className="h-12 bg-black/50 border border-white/10 rounded-none tracking-wider text-white px-4 outline-none focus:border-chart-3 w-full md:w-[200px]"
                value={examFilter}
                onChange={(e) => setExamFilter(e.target.value)}
              >
                <option value="ALL">ALL EXAMS</option>
                <option value="JEE">JEE</option>
                <option value="NEET">NEET</option>
                <option value="IAS">IAS</option>
                <option value="CAT">CAT</option>
              </select>
            </div>
          </div>

          <Card className="rounded-none border-border bg-card overflow-x-auto">
            <div className="min-w-[800px]">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-border bg-secondary font-bold text-xs uppercase tracking-widest text-muted-foreground">
                <div className="col-span-2 text-center">RANK</div>
                <div className="col-span-4">INSTITUTION</div>
                <div className="col-span-2 text-center">STUDENTS</div>
                <div className="col-span-2 text-center">TOTAL TESTS</div>
                <div className="col-span-2 text-right">AVG ACCURACY</div>
              </div>
              <div className="divide-y divide-border">
                {collegeLeaders.map(c => {
                  let badge = null;
                  if (c.rank <= 100) badge = <span className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/50 px-1.5 py-0.5 text-[10px] font-black tracking-widest rounded-sm flex items-center gap-1 shrink-0"><Medal size={12} weight="fill"/> GOLD</span>;
                  else if (c.rank <= 200) badge = <span className="bg-slate-300/10 text-slate-300 border border-slate-300/50 px-1.5 py-0.5 text-[10px] font-black tracking-widest rounded-sm flex items-center gap-1 shrink-0"><Medal size={12} weight="fill"/> SILVER</span>;
                  else if (c.rank <= 300) badge = <span className="bg-amber-700/20 text-amber-600 border border-amber-700/50 px-1.5 py-0.5 text-[10px] font-black tracking-widest rounded-sm flex items-center gap-1 shrink-0"><Medal size={12} weight="fill"/> BRONZE</span>;

                  return (
                    <div key={c.institution} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-secondary/30 transition-colors">
                      <div className="col-span-2 font-heading text-2xl text-chart-3 text-center">#{c.rank}</div>
                      <div className="col-span-4 font-bold flex items-center gap-3 truncate" title={c.institution}>
                        <div className="flex items-center gap-2 truncate">
                          <Buildings size={18} className="text-muted-foreground shrink-0"/> 
                          <span className="truncate">{c.institution}</span>
                        </div>
                        {badge}
                      </div>
                      <div className="col-span-2 text-center text-sm font-bold flex items-center justify-center gap-1">
                        <Users size={16} className="text-primary"/> {c.student_count}
                      </div>
                      <div className="col-span-2 text-center text-sm font-bold">{c.tests_taken}</div>
                      <div className="col-span-2 text-right font-bold text-chart-5">{c.accuracy}%</div>
                    </div>
                  );
                })}
                {collegeLeaders.length === 0 && <div className="p-8 text-center text-muted-foreground tracking-widest uppercase text-sm font-bold">NO INSTITUTION DATA AVAILABLE YET</div>}
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}